import { PageAction, PageActionToSave } from '@app-velocity/common/types';

import { isNodeWithReactions } from './isNodeWithReactions';
import { isNodeWithSize } from './isNodeWithSize';
import { NodeWithSize, SimpleTextNode } from '../types';
import { calculateOverlapPercentage } from './boundingBoxesOverlap';
import { getAbsoluteCoordinates } from './getAbsoluteCoordinates';
import { simpleUUID } from './simpleUUID';

const normalizeReaction = (trigger: Trigger | null): PageAction['trigger'] => {
  if (!trigger) return 'click';
  const { type } = trigger;

  switch (type) {
    case 'ON_CLICK':
      return 'click';
    case 'ON_HOVER':
      return 'hover';
    case 'AFTER_TIMEOUT':
      return 'delay';
    default:
      return 'click';
  }
};

const getTriggerData = (trigger: Trigger | null): PageAction['triggerData'] => {
  if (!trigger) return null;

  const { type } = trigger;

  switch (type) {
    case 'AFTER_TIMEOUT':
      return {
        timeout: trigger.timeout,
      };
    default:
      return null;
  }
};

const handleNavigateAction = (
  node: BaseNode,
  pageNode: BaseNode,
  trigger: Trigger | null,
  action: Action,
  nodeActions: PageActionToSave[],
  allTextNodes: SimpleTextNode[]
) => {
  if (action.type !== 'NODE') return;
  if (!action.destinationId) return;

  const normalizedReaction = normalizeReaction(trigger);

  if (!isNodeWithSize(pageNode) || !isNodeWithSize(node)) return;

  const parentPosition = getAbsoluteCoordinates(pageNode);
  const parentX = parentPosition.x;
  const parentY = parentPosition.y;

  const nodePosition = getAbsoluteCoordinates(node);

  const context: string[] = getContext(node, allTextNodes);
  const elementLabels =
    normalizedReaction !== 'delay'
      ? getElementTextLabel(node, allTextNodes)
      : [];
  nodeActions.push({
    id: simpleUUID(),
    action: 'navigate',
    to: action.destinationId,
    trigger: normalizedReaction,
    triggerData: getTriggerData(trigger),
    element: {
      id: node.id,
      x: nodePosition.x - parentX,
      y: nodePosition.y - parentY,
      width: node.width,
      height: node.height,
    },
    context,
    nextActionElementLabel:
      elementLabels.length === 1 ? elementLabels[0] : undefined,
    nextActionElementLabels:
      elementLabels.length > 1 ? elementLabels : undefined,
    nextActionElementCoordinates: {
      x: nodePosition.x - parentX + Math.round(node.width / 2),
      y: nodePosition.y - parentY + Math.round(node.height / 2),
    },
  });
};

const handleCloseOverlayAction = (
  node: BaseNode,
  pageNode: BaseNode,
  trigger: Trigger | null,
  action: Action,
  nodeActions: PageActionToSave[],
  allTextNodes: SimpleTextNode[]
) => {
  if (action.type !== 'CLOSE') return;

  const normalizedReaction = normalizeReaction(trigger);

  if (!isNodeWithSize(pageNode) || !isNodeWithSize(node)) return;

  const parentPosition = getAbsoluteCoordinates(pageNode);
  const parentX = parentPosition.x;
  const parentY = parentPosition.y;

  const nodePosition = getAbsoluteCoordinates(node);

  const ratio = 1;

  const context: string[] = getContext(node, allTextNodes);
  const elementLabels =
    normalizedReaction !== 'delay'
      ? getElementTextLabel(node, allTextNodes)
      : [];

  nodeActions.push({
    id: simpleUUID(),
    action: 'close-overlay',
    trigger: normalizedReaction,
    triggerData: getTriggerData(trigger),
    element: {
      id: node.id,
      x: nodePosition.x - parentX,
      y: nodePosition.y - parentY,
      width: node.width,
      height: node.height,
    },
    context,
    nextActionElementLabel:
      elementLabels.length === 1 ? elementLabels[0] : undefined,
    nextActionElementLabels:
      elementLabels.length > 1 ? elementLabels : undefined,
    nextActionElementCoordinates: {
      x: (nodePosition.x - parentX + Math.round(node.width / 2)) / ratio,
      y: (nodePosition.y - parentY + Math.round(node.height / 2)) / ratio,
    },
  });
};

const handleOverlayAction = (
  node: BaseNode,
  pageNode: BaseNode,
  trigger: Trigger | null,
  action: Action,
  nodeActions: PageActionToSave[],
  allTextNodes: SimpleTextNode[]
) => {
  if (action.type !== 'NODE') return;
  if (!action.destinationId) return;

  const normalizedReaction = normalizeReaction(trigger);

  if (!isNodeWithSize(pageNode) || !isNodeWithSize(node)) return;

  const parentPosition = getAbsoluteCoordinates(pageNode);
  const parentX = parentPosition.x;
  const parentY = parentPosition.y;

  const nodePosition = getAbsoluteCoordinates(node);

  const ratio = 1;

  const { x, y } = action.overlayRelativePosition || { x: 0, y: 0 };

  const context: string[] = getContext(node, allTextNodes);
  const elementLabels =
    normalizedReaction !== 'delay'
      ? getElementTextLabel(node, allTextNodes)
      : [];

  nodeActions.push({
    id: simpleUUID(),
    action: 'overlay',
    to: action.destinationId,
    trigger: normalizedReaction,
    triggerData: getTriggerData(trigger),
    element: {
      id: node.id,
      x: nodePosition.x - parentX,
      y: nodePosition.y - parentY,
      width: node.width,
      height: node.height,
    },
    context,
    nextActionElementLabel:
      elementLabels.length === 1 ? elementLabels[0] : undefined,
    nextActionElementLabels:
      elementLabels.length > 1 ? elementLabels : undefined,
    nextActionElementCoordinates: {
      x: (nodePosition.x - parentX + Math.round(node.width / 2)) / ratio,
      y: (nodePosition.y - parentY + Math.round(node.height / 2)) / ratio,
    },
    overlayCoordinates: {
      x: Math.round(x / ratio),
      y: Math.round(y / ratio),
    },
  });
};

// @TODO refactor
const handleSwapOverlayAction = (
  node: BaseNode,
  pageNode: BaseNode,
  trigger: Trigger | null,
  action: Action,
  nodeActions: PageActionToSave[],
  allTextNodes: SimpleTextNode[]
) => {
  if (action.type !== 'NODE') return;
  if (!action.destinationId) return;

  const normalizedReaction = normalizeReaction(trigger);

  if (!isNodeWithSize(pageNode) || !isNodeWithSize(node)) return;

  const parentPosition = getAbsoluteCoordinates(pageNode);
  const parentX = parentPosition.x;
  const parentY = parentPosition.y;

  const nodePosition = getAbsoluteCoordinates(node);

  const ratio = 1;

  const { x, y } = action.overlayRelativePosition || { x: 0, y: 0 };

  const context: string[] = getContext(node, allTextNodes);
  const elementLabels =
    normalizedReaction !== 'delay'
      ? getElementTextLabel(node, allTextNodes)
      : [];

  nodeActions.push({
    id: simpleUUID(),
    action: 'swap-overlay',
    to: action.destinationId,
    trigger: normalizedReaction,
    triggerData: getTriggerData(trigger),
    element: {
      id: node.id,
      x: nodePosition.x - parentX,
      y: nodePosition.y - parentY,
      width: node.width,
      height: node.height,
    },
    context,
    nextActionElementLabel:
      elementLabels.length === 1 ? elementLabels[0] : undefined,
    nextActionElementLabels:
      elementLabels.length > 1 ? elementLabels : undefined,
    nextActionElementCoordinates: {
      x: (nodePosition.x - parentX + Math.round(node.width / 2)) / ratio,
      y: (nodePosition.y - parentY + Math.round(node.height / 2)) / ratio,
    },
    overlayCoordinates: {
      x: Math.round(x / ratio),
      y: Math.round(y / ratio),
    },
  });
};

const handleChangeToAction = async (
  node: BaseNode,
  pageNode: BaseNode,
  trigger: Trigger | null,
  action: Action,
  nodeActions: PageActionToSave[],
  allTextNodes: SimpleTextNode[]
) => {
  if (action.type !== 'NODE') return;
  if (!action.destinationId) return;
  // this node or ancestor
  // "CHANGE_TO": Changes the closest ancestor instance of source node to the specified variant.
  const closestInstance = await findClosestAncestorInstance(
    node,
    action.destinationId
  );

  if (!closestInstance) return;

  const normalizedReaction = normalizeReaction(trigger);

  if (!isNodeWithSize(pageNode) || !isNodeWithSize(node)) return;

  const parentPosition = getAbsoluteCoordinates(pageNode);
  const parentX = parentPosition.x;
  const parentY = parentPosition.y;

  const ratio = 1;

  const { x, y } = action.overlayRelativePosition || { x: 0, y: 0 };

  const nodePosition = getAbsoluteCoordinates(node);
  const nodeX = nodePosition.x - parentX;
  const nodeY = nodePosition.y - parentY;
  const context: string[] = getContext(node, allTextNodes);

  const elementLabels =
    normalizedReaction !== 'delay'
      ? getElementTextLabel(node, allTextNodes)
      : [];

  nodeActions.push({
    id: simpleUUID(),
    action: 'change',
    to: action.destinationId,
    from: closestInstance.id,
    trigger: normalizedReaction,
    triggerData: getTriggerData(trigger),
    element: {
      id: node.id,
      x: nodeX,
      y: nodeY,
      width: node.width,
      height: node.height,
    },
    context,
    nextActionElementLabel:
      elementLabels.length === 1 ? elementLabels[0] : undefined,
    nextActionElementLabels:
      elementLabels.length > 1 ? elementLabels : undefined,
    nextActionElementCoordinates: {
      x: (nodeX + Math.round(node.width / 2)) / ratio,
      y: (nodeY + Math.round(node.height / 2)) / ratio,
    },
    overlayCoordinates: {
      x: Math.round(x / ratio),
      y: Math.round(y / ratio),
    },
  });
};

const handleSetVariableAction = (
  node: BaseNode,
  pageNode: BaseNode,
  trigger: Trigger | null,
  action: Action,
  nodeActions: PageActionToSave[],
  allTextNodes: SimpleTextNode[]
) => {
  if (action.type !== 'SET_VARIABLE') return;
  const normalizedReaction = normalizeReaction(trigger);

  const actionBase: PageActionToSave = {
    id: simpleUUID(),
    action: 'set-variable',
    variable: action.variableId,
    variableValue: action.variableValue?.value,
    trigger: normalizedReaction,
    triggerData: getTriggerData(trigger),
  };

  if (!isNodeWithSize(pageNode) || !isNodeWithSize(node)) {
    nodeActions.push(actionBase);

    return;
  }

  const parentPosition = getAbsoluteCoordinates(pageNode);
  const parentX = parentPosition.x;
  const parentY = parentPosition.y;

  const nodePosition = getAbsoluteCoordinates(node);
  const context: string[] = getContext(node, allTextNodes);

  const elementLabels =
    normalizedReaction !== 'delay'
      ? getElementTextLabel(node, allTextNodes)
      : [];

  nodeActions.push({
    ...actionBase,
    element: {
      id: node.id,
      x: nodePosition.x - parentX,
      y: nodePosition.y - parentY,
      width: node.width,
      height: node.height,
    },
    context,
    nextActionElementLabel:
      elementLabels.length === 1 ? elementLabels[0] : undefined,
    nextActionElementLabels:
      elementLabels.length > 1 ? elementLabels : undefined,
    nextActionElementCoordinates: {
      x: nodePosition.x - parentX + Math.round(node.width / 2),
      y: nodePosition.y - parentY + Math.round(node.height / 2),
    },
  });
};

export const getContext = (
  node: NodeWithSize,
  allTextNodes: SimpleTextNode[]
) => {
  const nodes = allTextNodes.map((textNode, index) => {
    const nodePosition = getAbsoluteCoordinates(node);
    const textNodePosition = textNode.absolutePosition;
    const distance = Math.sqrt(
      Math.abs(
        nodePosition.x +
          node.width / 2 -
          (textNodePosition.x + textNode.width / 2)
      ) **
        2 +
        Math.abs(
          nodePosition.y +
            node.height / 2 -
            (textNodePosition.y + textNode.height / 2)
        ) **
          2
    );

    return {
      x: textNodePosition.x,
      y: textNodePosition.y,
      nodeX: nodePosition.x,
      nodeY: nodePosition.y,
      distance,
      text: textNode.characters,
    };
  });

  const sortedNodes = nodes.sort((a, b) => a.distance - b.distance);

  return sortedNodes.slice(0, 5).map((node) => node.text);
};

const getElementTextLabel = (
  node: BaseNode,
  allTextNodes: SimpleTextNode[]
) => {
  if (node.type === 'TEXT') return [node.characters];

  if (!isNodeWithSize(node)) return [];

  const nodePosition = getAbsoluteCoordinates(node);

  const visuallyNestedNodes = allTextNodes.filter((textNode) => {
    if (textNode.id === node.id) return false;
    const textNodePosition = textNode.absolutePosition;

    const overlap = calculateOverlapPercentage(
      {
        x: nodePosition.x,
        y: nodePosition.y,
        width: node.width,
        height: node.height,
      },
      {
        x: textNodePosition.x,
        y: textNodePosition.y,
        width: textNode.width,
        height: textNode.height,
      }
    );

    if (overlap > 0.95) return true;

    return false;
  });

  return visuallyNestedNodes.map((textNode) => textNode.characters);
};

const findClosestAncestorInstance = async (
  node: BaseNode,
  destinationId: string
): Promise<InstanceNode | null> => {
  if (node.type === 'INSTANCE') {
    const mainComponent = await node.getMainComponentAsync();

    const availableVariants = mainComponent?.parent?.children || [];

    const availableVariantsIds = availableVariants.map((v) => v.id);

    if (availableVariantsIds.includes(destinationId)) return node;
  }

  let currentNode: BaseNode | null = node.parent;

  while (currentNode && currentNode.type !== 'PAGE') {
    if (currentNode.type === 'INSTANCE') {
      const mainComponent = await currentNode.getMainComponentAsync();
      const availableVariants = mainComponent?.parent?.children || [];
      const availableVariantsIds = availableVariants.map((v) => v.id);

      if (availableVariantsIds.includes(destinationId)) {
        return currentNode;
      }
    }

    if (!currentNode.parent) return null;

    currentNode = currentNode.parent;
  }

  return null;
};

const traverseReactions = async (
  nodeWithReactions: BaseNode,
  originalNode: BaseNode,
  pageNode: BaseNode,
  nodeActions: PageActionToSave[],
  allTextNodes: SimpleTextNode[]
) => {
  if ('opacity' in originalNode && !originalNode.opacity) return;
  if ('visible' in originalNode && !originalNode.visible) return;

  if (
    isNodeWithReactions(nodeWithReactions) &&
    nodeWithReactions.reactions.length > 0
  ) {
    for (const reaction of nodeWithReactions.reactions) {
      if (!reaction.actions) continue;

      for (const action of reaction.actions) {
        switch (action.type) {
          case 'NODE': {
            if (!action.destinationId) continue;

            switch (action.navigation) {
              case 'NAVIGATE': {
                handleNavigateAction(
                  originalNode,
                  pageNode,
                  reaction.trigger,
                  action,
                  nodeActions,
                  allTextNodes
                );
                break;
              }

              case 'OVERLAY': {
                handleOverlayAction(
                  originalNode,
                  pageNode,
                  reaction.trigger,
                  action,
                  nodeActions,
                  allTextNodes
                );
                break;
              }

              case 'SWAP': {
                handleSwapOverlayAction(
                  originalNode,
                  pageNode,
                  reaction.trigger,
                  action,
                  nodeActions,
                  allTextNodes
                );
                break;
              }

              case 'CHANGE_TO': {
                await handleChangeToAction(
                  originalNode,
                  pageNode,
                  reaction.trigger,
                  action,
                  nodeActions,
                  allTextNodes
                );
                break;
              }
            }

            continue;
          }

          case 'SET_VARIABLE': {
            handleSetVariableAction(
              originalNode,
              pageNode,
              reaction.trigger,
              action,
              nodeActions,
              allTextNodes
            );
            continue;
          }

          case 'CLOSE': {
            handleCloseOverlayAction(
              originalNode,
              pageNode,
              reaction.trigger,
              action,
              nodeActions,
              allTextNodes
            );
            continue;
          }

          default:
            continue;
        }
      }
    }
  }
};

// for reading variant actions a new instance needs to be created to read the correct actions, for other cases nodeWithReactions === originalNode
export const readReactions = async (
  nodeWithReactions: BaseNode,
  originalNode: BaseNode,
  pageNode: BaseNode,
  allTextNodes: SimpleTextNode[]
) => {
  const nodeActions: PageActionToSave[] = [];

  await traverseReactions(
    nodeWithReactions,
    originalNode,
    pageNode,
    nodeActions,
    allTextNodes
  );

  return nodeActions;
};
